<docs>
A checkbox for a value that is applied when the form is submitted. A real `<input type="checkbox">`
with `appearance-none`, not a Reka primitive: the platform provides the whole interaction, so form
submission, bubbling `change` (vee-validate's ValidationProvider sees it), the native `indeterminate`
property, keyboard and a11y all stay native. For a setting that applies immediately use ZoneToggle;
ZoneSegmentedControl / ZoneSelect pick one of several options.

The base form is just the box: `<ZoneCheckbox v-model="on" />` renders a lone checkbox and the
wrapper goes `inline-flex`, so it sits in a table cell or a toolbar like any inline control. It
becomes a stacked block once it carries a `fieldLabel` or a helper line. Pass `ariaLabel` when there
is no label of any kind.

## Labels

Two different labels, and the naming differs from every other Zone field. `label` is the text beside
the box (Figma body-sm, 14px/20px gray-900): the checkbox's own accessible name. Every other Zone
field (Toggle, Select, TextInput) uses `label` for the uppercase row; here that row is `fieldLabel`,
the optional 12px gray-600 ZoneLabel above the control (Figma's "Label" element, off by default), for
a checkbox that is one row of a titled group.

The required `*` renders in whichever of the two is the outermost label, so it never shows up twice:
the `fieldLabel` row when there is one, otherwise beside `label`. The `label` copy is ZoneLabel's
markup verbatim (inside the `<label>`, `aria-hidden`, no colour of its own): change it with
ZoneLabel's and ZoneSelect's, not alone.

## Notes

- Rails: an unchecked checkbox submits nothing, so pair it with a hidden false field when the backend
  needs one (Rails' own `check_box` helper does this). `value` defaults to `'on'`; set it for array
  params, e.g. `name="ids[]" value="42"`.
- Clicking the text toggles the box through `for`/`id`, so no wrapping `<label>` is needed. `id`
  falls back to a generated one whenever the caller omits it.
- `indeterminate` is a DOM property with no attribute. A native click clears it, so it is
  re-asserted from the prop after every change: an owner that keeps the prop true keeps the mixed
  state.
- `readonly` cancels the click instead of undoing it afterwards, so a bubbling `change` never reaches
  the form at all. Both the pointer and the Space key activate through `click`; `onChange` still
  guards the programmatic path, which skips it.
- The check glyph is a Material Symbols ligature, real text, so it is wrapped in an `aria-hidden`
  span to keep it out of the accessible name.
- Focus a11y gap: Figma draws focus as a 2px primary-700 frame, the same blue as the checked fill,
  so a focused checked box reads only as a slightly larger blue square. Rendered as specced; revisit
  with design if it tests badly.
- `warning` and `invalid` colour the helper line; only `invalid` also reaches the box.

Figma: [Checkbox](https://www.figma.com/design/eOrg3dHaQFZ7VdCZCcomNC/-Zone-DS--Components-Specs?node-id=1525-30817&m=dev),
[Tokens & Components](https://www.figma.com/design/DD0eEumGwzGfrPbb7q2iak/-Zone--Tokens---Components?node-id=439-64552&m=dev)
</docs>

<template>
  <div :class="wrapperClasses">
    <div
      v-if="hasFieldLabel"
      data-testid="field-label"
    >
      <slot
        v-if="$slots['field-label']"
        name="field-label"
        :html-for="resolvedId"
        :required="required"
        :invalid="invalid"
        :tooltip="tooltip"
      />
      <ZoneLabel
        v-else
        :label="fieldLabel"
        :required="required"
        :invalid="invalid"
        :tooltip="tooltip"
        :icon="Boolean(tooltip)"
        :html-for="resolvedId"
      />
    </div>

    <div class="flex items-center gap-2 py-0.5">
      <span class="relative flex shrink-0 items-center justify-center">
        <input
          :id="resolvedId"
          ref="input"
          type="checkbox"
          :class="boxClasses"
          :name="name"
          :value="value"
          :checked="modelValue"
          :indeterminate="indeterminate"
          :disabled="disabled"
          :required="required"
          :aria-label="ariaLabel || undefined"
          :aria-checked="indeterminate ? 'mixed' : undefined"
          :aria-invalid="invalid || undefined"
          :aria-readonly="readonly || undefined"
          :aria-describedby="hasHelper ? helperId : undefined"
          @click="onClick"
          @change="onChange"
        />
        <span
          v-if="glyph"
          class="pointer-events-none absolute inset-0 flex items-center justify-center leading-none text-white"
          aria-hidden="true"
          data-testid="checkbox-glyph"
        >
          <ZoneIcon :icon="glyph" />
        </span>
      </span>

      <slot
        v-if="$slots.label"
        name="label"
        :html-for="resolvedId"
        :required="required"
        :invalid="invalid"
      />
      <label
        v-else-if="label"
        :for="resolvedId"
        :class="labelClasses"
        data-testid="checkbox-label"
      >
        {{ label }}<span
          v-if="required && !hasFieldLabel"
          class="ml-0.5"
          aria-hidden="true"
          data-testid="required-asterisk"
        >*</span>
      </label>

      <span
        v-if="hasInlineTooltip"
        class="flex shrink-0 items-center"
        data-testid="inline-tooltip"
      >
        <ZoneIcon
          icon="info"
          :tooltip="tooltip"
          :class="invalid ? 'text-red-600' : 'text-gray-600'"
        />
      </span>
    </div>

    <HelperText
      v-if="hasHelper"
      :id="helperId"
      :invalid="invalid"
      :warning="warning"
    >
      <slot name="helper">{{ helperText }}</slot>
    </HelperText>
  </div>
</template>

<script lang="ts">
import { computed, nextTick, ref, useId } from 'vue';
import ZoneIcon from './Icon.vue';
import HelperText from './field/HelperText.vue';
import ZoneLabel from './Label.vue';

</script>

<script setup lang="ts">
defineOptions({ name: 'ZoneCheckbox' });

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  /**
   * Figma's "Intermediate" state, a partial selection (e.g. a select-all over a page where some
   * rows are picked). Beats `modelValue` for the glyph, so a box that is both reads as
   * indeterminate.
   */
  indeterminate: {
    type: Boolean,
    default: false,
  },
  /**
   * The text beside the box, the checkbox's accessible name. Deliberately not the uppercase row
   * that `label` means on every other Zone field; that one is `fieldLabel` here.
   */
  label: {
    type: String,
    default: null,
  },
  /** The optional uppercase ZoneLabel row above the control. */
  fieldLabel: {
    type: String,
    default: null,
  },
  /** Small text below the row (or the `#helper` slot). */
  helperText: {
    type: String,
    default: null,
  },
  /** Info-tooltip text on the field label. */
  tooltip: {
    type: String,
    default: null,
  },
  /** Accessible name when there is no label of any kind. */
  ariaLabel: {
    type: String,
    default: null,
  },
  /** Native form name. */
  name: {
    type: String,
    default: null,
  },
  /** What a checked box submits; unchecked submits nothing, as any native checkbox. */
  value: {
    type: String,
    default: 'on',
  },
  /** Falls back to a generated id. */
  id: {
    type: String,
    default: null,
  },
  required: {
    type: Boolean,
    default: false,
  },
  /** Non-interactive, whole field at 30% opacity, submits nothing. */
  disabled: {
    type: Boolean,
    default: false,
  },
  /**
   * Shows a value the user cannot change: grey box, still focusable and still submitted. Not the
   * same as `disabled`.
   */
  readonly: {
    type: Boolean,
    default: false,
  },
  /** Red box border, red helper text, error icon and `aria-invalid`. */
  invalid: {
    type: Boolean,
    default: false,
  },
  /** Yellow helper text and warning icon (suppressed by `invalid`). */
  warning: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits<{
  /** On toggle. */
  'update:modelValue': [checked: boolean];
  /** Fires with `update:modelValue`, for form wiring. */
  change: [checked: boolean];
}>();

const slots = defineSlots<{
  /**
   * Replaces the text beside the box. It does not render inside a `<label>` of ours: render your own
   * ZoneLabel in it to keep the standard look.
   */
  label?(props: { htmlFor: string; required: boolean; invalid: boolean }): any;
  /** Replaces the uppercase ZoneLabel row, same rule as `#label`. */
  'field-label'?(props: { htmlFor: string; required: boolean; invalid: boolean; tooltip: string | null }): any;
  /** The helper line; overrides `helperText`. */
  helper?(): any;
}>();

const input = ref<HTMLInputElement | null>(null);

const generatedId = `zone-checkbox-${useId()}`;

const resolvedId = computed<string>(() => props.id || generatedId);

const helperId = computed<string>(() => `${resolvedId.value}-helper`);

const hasFieldLabel = computed<boolean>(() => Boolean(props.fieldLabel || slots['field-label']));

const hasHelper = computed<boolean>(() => Boolean(props.helperText || slots.helper));

// Figma only models the info icon inside the uppercase label row. With no such row there is
// nowhere for it to go, so rather than dropping the tooltip it sits inline after the text.
const hasInlineTooltip = computed<boolean>(() => Boolean((props.tooltip) && !hasFieldLabel.value));

const glyph = computed<string | null>(() => {
  if (props.indeterminate) {
    return 'check_indeterminate_small';
  }

  return props.modelValue ? 'check_small' : null;
});

// A bare checkbox behaves like an inline control (a table cell, a toolbar) rather than a
// block claiming the full row. Figma's own min/max widths belong to the canvas frame, not
// the component, so the caller owns width — same call as ZoneToggle.
const wrapperClasses = computed<string>(() => {
  const stacked = hasFieldLabel.value || hasHelper.value;
  const classes = [stacked ? 'flex flex-col gap-2' : 'inline-flex'];

  if (props.disabled) {
    classes.push('opacity-30');
  }

  return classes.join(' ');
});

// Straight from the Figma matrix. `border-transparent` rather than dropping the border keeps
// every variant the same 16px, so the box never jumps by a pixel as it fills.
// Warning and Disabled reuse the default colours — warning shows only in the helper line,
// and disabled is the default box under the wrapper's 30% opacity.
const boxColorClasses = computed<string>(() => {
  const filled = props.modelValue || props.indeterminate;

  if (props.readonly) {
    return filled ? 'border-transparent bg-gray-500' : 'border-gray-300 bg-gray-100';
  }

  if (props.invalid) {
    return filled ? 'border-red-600 bg-primary-700' : 'border-red-600';
  }

  return filled ? 'border-transparent bg-primary-700' : 'border-gray-300';
});

// Figma nests a 16px "Contents" square inside an 18px focus frame. One element does both
// here: the box is the 16px square, and the focus outline straddles its edge (2px wide,
// pulled in 1px) so the focused footprint is exactly Figma's 18px frame.
const boxClasses = computed<string>(() => {
  const classes = [
    'm-0 h-4 w-4 shrink-0 appearance-none rounded border outline-none',
    // `focus-visible:outline-solid` is load-bearing, not redundant: Tailwind v4 emits
    // `outline-2` as `outline-style: var(--tw-outline-style)`, and the base `outline-none`
    // sets that variable to `none` on this same element — so without restoring it here the
    // focus ring would silently never paint. Verified against the generated CSS.
    'focus:ring-0 focus-visible:outline-solid focus-visible:outline-2',
    'focus-visible:outline-primary-700 -outline-offset-1',
    boxColorClasses.value,
  ];

  if (props.disabled) {
    classes.push('cursor-not-allowed');
  } else if (props.readonly) {
    classes.push('cursor-default');
  } else {
    classes.push('cursor-pointer');
  }

  return classes.join(' ');
});

// `m-0` and `font-regular` are load-bearing, not tidiness: two global bare-element rules
// (`label { margin-bottom: 5px; font-weight: 700 }` and `label { font-weight: 500 }`) beat
// anything merely inherited. See the NOTES block in Label.vue.
const labelClasses = computed<string>(() => {
  const classes = ['m-0 min-w-0 flex-1 text-sm font-regular leading-5 text-gray-900'];

  if (props.disabled) {
    classes.push('cursor-not-allowed');
  } else if (props.readonly) {
    classes.push('cursor-default');
  } else {
    classes.push('cursor-pointer');
  }

  return classes.join(' ');
});



// Native checkboxes have no readonly, and `disabled` would drop the box out of the tab order
// and stop it submitting — so activation is cancelled instead. Both the pointer and the Space
// key activate through a click event, and cancelling it reverts the checkedness the browser
// had already flipped (and the `indeterminate` it had already cleared) BEFORE any `change`
// fires, so nothing downstream — a bubbling listener, vee-validate — ever sees a toggle.
function onClick(event: MouseEvent) {
  if (props.readonly) {
    event.preventDefault();
  }
}

function syncIndeterminate() {
  if (input.value) {
    input.value.indeterminate = props.indeterminate;
  }
}

function onChange(event: Event) {
  const target = event.target as HTMLInputElement;

  // Belt and braces for the paths that skip activation: a programmatic `checked = x` plus a
  // dispatched `change`, which `onClick` never sees. The component is fully controlled, so
  // swallowing the update and putting the DOM node back is enough to freeze it.
  if (props.readonly) {
    target.checked = props.modelValue;
    syncIndeterminate();

    return;
  }

  emit('update:modelValue', target.checked);
  emit('change', target.checked);

  // A native click clears `indeterminate`, and Vue will not patch a prop whose value did not
  // change — so an owner that keeps `indeterminate` true needs it re-asserted by hand.
  nextTick(syncIndeterminate);
}
</script>
